import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { ROOT_URL } from "../../api/ApiConfig";
import { mapConversationsToRows } from "../../utils/conversationHistoryMappers";

const emptyPagination = {
  page: 1,
  limit: 10,
  total: 0,
  total_pages: 0,
  from: 0,
  to: 0,
  preview: false,
};

/**
 * GET /customer/history/:customerId
 * Full conversations modal (pass customer id).
 */
export const getCustomerConversationHistory = createAsyncThunk(
  "customer/getCustomerConversationHistory",
  async (
    { customerId, page = 1, limit = 10, search, type, status } = {},
    { getState, rejectWithValue }
  ) => {
    if (!customerId) return rejectWithValue("Missing customerId");
    try {
      const { loginToken } = getState();
      const authToken = loginToken?.response?.token;
      const params = new URLSearchParams();
      params.set("page", String(page));
      params.set("limit", String(limit));
      if (search) params.set("search", search);
      if (type && type !== "all") params.set("type", type);
      if (status && status !== "all") params.set("status", status);

      const response = await axios.get(
        `${ROOT_URL}/customer/history/${customerId}?${params.toString()}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
        }
      );
      return { ...response.data, _customerId: String(customerId) };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const initialState = {
  loading: false,
  error: null,
  customerId: null,
  customer: null,
  summary: { total: 0, label: "" },
  conversations: [],
  rows: [],
  pagination: emptyPagination,
};

const getCustomerConversationHistorySlice = createSlice({
  name: "getCustomerConversationHistory",
  initialState,
  reducers: {
    resetCustomerConversationHistory: () => ({ ...initialState }),
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCustomerConversationHistory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCustomerConversationHistory.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload?.data || {};
        state.customerId = action.payload?._customerId || state.customerId;
        state.customer = payload.customer || null;
        state.summary = payload.summary || { total: 0, label: "" };
        state.conversations = Array.isArray(payload.conversations)
          ? payload.conversations
          : [];
        state.rows = mapConversationsToRows(state.conversations);
        state.pagination = payload.pagination || emptyPagination;
      })
      .addCase(getCustomerConversationHistory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { resetCustomerConversationHistory } =
  getCustomerConversationHistorySlice.actions;
export default getCustomerConversationHistorySlice.reducer;
